/**
 * Admin Context Provider
 *
 * Provides tenant context switching for super admins AND implicit tenant
 * context detection for SSO users via JWT claims.
 *
 * Phase 4.5: Tenant Context Entry (WQ-055)
 * WQ-089: Broadened isInTenantContext to detect JWT implicit tenant context
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';

/** Minimal tenant data needed for context switching */
export interface TenantContext {
  id: string;
  name: string;
  slug: string;
  branding?: {
    primaryColor?: string;
  };
}

/** How the tenant context was established */
export type TenantContextSource = 'explicit' | 'jwt' | null;

/** Value provided by the admin context */
export interface AdminContextValue {
  activeTenant: TenantContext | null;
  isInTenantContext: boolean;
  /** 'explicit' = super admin entered tenant, 'jwt' = SSO user with tid claim, null = no context */
  tenantContextSource: TenantContextSource;
  isLoading: boolean;
  enterTenant: (tenant: TenantContext) => void;
  exitTenant: () => void;
  canSwitchContext: boolean;
  /** Incremented when nav overrides change — triggers useEffectiveSchema re-fetch */
  schemaVersion: number;
  incrementSchemaVersion: () => void;
}

const defaultValue: AdminContextValue = {
  activeTenant: null,
  isInTenantContext: false,
  tenantContextSource: null,
  isLoading: false,
  enterTenant: () => {},
  exitTenant: () => {},
  canSwitchContext: false,
  schemaVersion: 0,
  incrementSchemaVersion: () => {},
};

const AdminContext = createContext<AdminContextValue>(defaultValue);

/**
 * Hook to access the admin tenant context.
 * Must be used within an AdminContextProvider.
 */
export function useAdminContext(): AdminContextValue {
  return useContext(AdminContext);
}

/**
 * Provider component that manages tenant context state.
 * Wrap the app (or admin portal) with this to enable context switching.
 */
export const AdminContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeTenant, setActiveTenant] = useState<TenantContext | null>(null);
  const [schemaVersion, setSchemaVersion] = useState(0);
  const { tenantId: jwtTenantId, user } = useAuth();

  const incrementSchemaVersion = useCallback(() => {
    setSchemaVersion(v => v + 1);
  }, []);

  const enterTenant = useCallback((tenant: TenantContext) => {
    setActiveTenant({
      id: tenant.id,
      name: tenant.name,
      slug: tenant.slug,
      branding: tenant.branding,
    });
  }, []);

  const exitTenant = useCallback(() => {
    setActiveTenant(null);
  }, []);

  // WQ-089 fix: Super admins IGNORE their JWT tenant ID for context purposes.
  // They always belong to Default Org (so tid is non-null), but they operate
  // at platform level unless they explicitly enter a tenant via "Enter Tenant".
  // Only non-super-admin users (SSO tenant users) use JWT for implicit context.
  // Guard: user must be loaded before we can check their role. If user is null
  // (e.g., during auth restore), never assume JWT tenant context — wait for user.
  const userRole = user?.role?.toLowerCase() || '';
  const isSuperAdminUser = userRole === 'super_admin' || userRole === 'superadmin';
  const effectiveJwtTenantId = (!user || isSuperAdminUser) ? null : jwtTenantId;

  // WQ-089: Detect tenant context from EITHER explicit selection (super admin
  // "Enter Tenant") OR implicit JWT claims (SSO tenant user login with tid).
  const tenantContextSource: TenantContextSource = useMemo(() => {
    if (activeTenant) return 'explicit';
    if (effectiveJwtTenantId) return 'jwt';
    return null;
  }, [activeTenant, effectiveJwtTenantId]);

  const isInTenantContext = tenantContextSource !== null;

  const value: AdminContextValue = {
    activeTenant,
    isInTenantContext,
    tenantContextSource,
    isLoading: false,
    enterTenant,
    exitTenant,
    canSwitchContext: true,
    schemaVersion,
    incrementSchemaVersion,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContextProvider;
